import {match} from 'path-to-regexp'

import {
  getCookie,
  removeCookie,
  setCookie
} from '@vio/core/src/_web/utils/cookies'
import {getUrlParam} from '@vio/core/src/utils/url'

import {CookieConfig} from '../../../../../types/Cookie'
import {
  APPath,
  BMPath,
  CPPath,
  HPPath,
  PPPath,
  SIMULATE_PARAM,
  SRPPath
} from '../../constants'
import {Pages} from '../../types/enums'
import {
  ScenarioConfig,
  ScenarioMetadata,
  ScenarioVariations
} from '../../types/types'
import {simulationConfig} from './cookieConfig'

import type {RequestHandler} from 'msw'

const getCookieOptions = (cookieConfig: CookieConfig) => {
  const {secure, sameSite, maxAge: expires, domain} = cookieConfig.options
  return {
    expires,
    secure,
    sameSite,
    domain
  }
}
export const getPersistedSimulationFlag = () => {
  const simulationFlag = getCookie(simulationConfig.name)
  return simulationFlag
}

export const persistSimulation = (simulationFlag: string) => {
  setCookie(
    simulationConfig.name,
    simulationFlag,
    getCookieOptions(simulationConfig)
  )
}

export const removeSimulation = () => {
  removeCookie(simulationConfig.name)
}

export const buildScenarioFlag = (
  scenarioValue?: string,
  variation?: string
) => {
  return variation
    ? `${scenarioValue}${variation && `.${variation}`}`
    : scenarioValue
}

export const parseScenarioFlag = (
  combined: string
): {scenarioValue: string; variation: string | null} => {
  const parts = combined.split('.')

  return {
    scenarioValue: parts[0],
    variation: parts.length > 1 ? parts.slice(1).join('.') : null
  }
}

export const getUrlSimulationFlag = () => {
  return getUrlParam({
    search: window.location.search,
    param: SIMULATE_PARAM
  })
}

/**
 * Get simulation param from server context
 */
export const getSimulationFlag = (
  queryParams: URLSearchParams,
  cookies: Record<string, string>
) => !!cookies[simulationConfig.name] || queryParams.has(SIMULATE_PARAM)

export const getCurrentPage = () => {
  const {pathname} = window.location

  // SRP must come before AP — '/Hotel/Search' would otherwise match '/Hotel/:hotelSlug/:locale'
  const matchers = [
    [SRPPath, Pages.SRP],
    [APPath, Pages.AP],
    [PPPath, Pages.PP],
    [CPPath, Pages.CP],
    [BMPath, Pages.BM],
    [HPPath, Pages.HP]
  ]

  for (const [path, page] of matchers) {
    if (match(path)(pathname)) {
      return page
    }
  }
}

export const registerHandlers = (
  scenarioMetadata: ScenarioMetadata,
  paramName: string,
  variation: string,
  loadScenarioRouteIntoHandlers: (
    paramName: string,
    scenarioFilename: string,
    route: ScenarioConfig,
    variation: string
  ) => Promise<RequestHandler | undefined>
) => {
  return scenarioMetadata.routes.map(async route => {
    const scenarioFilename = `${route.method}${route.endpoint}${route.page}`
    const variationFilename = `${scenarioFilename}.${variation}`

    try {
      if (variation) {
        return await loadScenarioRouteIntoHandlers(
          paramName,
          variationFilename,
          route,
          variation
        )
      }
    } catch {
      return await loadScenarioRouteIntoHandlers(
        paramName,
        scenarioFilename,
        route,
        variation
      )
    }

    return await loadScenarioRouteIntoHandlers(
      paramName,
      scenarioFilename,
      route,
      variation
    )
  })
}

export const getSimulationFlags = (fullFlag: string): string[] => {
  return fullFlag.split('.')
}

export const getAllVariationsFromMetadata = (
  metadata: ScenarioMetadata
): ScenarioVariations[] => {
  let variations: ScenarioVariations[] = []
  metadata?.routes.forEach(
    route => (variations = [...variations, ...(route.variations ?? [])])
  )
  const dedupedVariations = [...new Set(variations)]
  return dedupedVariations.length ? dedupedVariations : []
}
